import {
  AlertDialog,
  AlertDialogProps,
  Dialog,
  DialogProps,
} from '@branch-messenger/willow-ui'
import { createContext, FC, ReactNode, useCallback, useState } from 'react'

import { DialogContextType } from '@/types/dialog'

export const DialogContext = createContext<DialogContextType>({
  showAlert: () => {},
  hideAlert: () => {},
  showDialog: () => {},
  hideDialog: () => {},
})

const initialAlertProps: AlertDialogProps = {
  title: '',
  description: '',
  actionDescription: '',
  cancelDescription: '',
  handleAction: () => {},
}

const initialDialogProps: DialogProps = {
  title: '',
  description: '',
  children: null,
}

type Props = {
  children: ReactNode
}

export const DialogProvider: FC<Props> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [alertProps, setAlertProps] = useState(initialAlertProps)
  const [dialogProps, setDialogProps] = useState(initialDialogProps)

  const hideAlert = useCallback(() => {
    setAlertProps(initialAlertProps)
    setOpen(false)
  }, [])

  const showDialog = useCallback(
    (props: DialogProps) => {
      hideAlert()
      setDialogProps(props)
      setDialogOpen(true)
    },
    [hideAlert]
  )

  const hideDialog = useCallback(() => {
    setDialogProps(initialDialogProps)
    setDialogOpen(false)
  }, [])

  const showAlert = useCallback(
    (props: AlertDialogProps) => {
      hideDialog()
      setAlertProps(props)
      setOpen(true)
    },
    [hideDialog]
  )

  const dialogContextValues = {
    showAlert,
    hideAlert,
    showDialog,
    hideDialog,
  }

  return (
    <DialogContext value={dialogContextValues}>
      <AlertDialog
        {...alertProps}
        onOpenChange={hideAlert}
        open={open}
        size="md"
      />
      <Dialog {...dialogProps} open={dialogOpen} onOpenChange={hideDialog}>
        {dialogProps.children}
      </Dialog>
      {children}
    </DialogContext>
  )
}
